import { FC, lazy, ReactNode, Suspense } from 'react';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { UIProviders } from '@trukt/ui/UIContext';
import { ApiProvider } from '@trukt/react-hooks/ApiProvider';
import { MsalInstanceProvider } from '@trukt/auth-web/MsalInstanceProvider';
import { MutationsProvider } from '@trukt/mutations/MutationsContext';
import { useNavigate } from '@tanstack/react-router';
import { msalInstance } from '@shared/auth';

import { truktApi } from '@shared/api/truktApi';
import { truktTmsApi } from '@shared/api/truktTmsApi';
import { trimbleMapsSingleSearchApi } from '@shared/api/trimbleMapsSingleSearchApi';

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(async () => {
      const { ReactQueryDevtools } = await import('@tanstack/react-query-devtools');

      return { default: ReactQueryDevtools };
    })
  : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: true,
      refetchIntervalInBackground: false,
    },
    mutations: { retry: 0 },
  },
});
const authenticatedApis = [truktApi, truktTmsApi];

export const AppProviders: FC<Record<string, never | ReactNode>> = ({ children }) => {
  const routerNavigate = useNavigate();
  const navigate = (path: string, options?: { replace?: boolean }) =>
    routerNavigate({ to: path, replace: options?.replace });

  return (
    <MsalInstanceProvider instance={msalInstance} apis={authenticatedApis} navigate={navigate}>
      <ApiProvider truktApi={truktTmsApi} trimbleApi={trimbleMapsSingleSearchApi}>
        <UIProviders appVariant="dispatcher">
          <QueryClientProvider client={queryClient}>
            <MutationsProvider truktTmsApi={truktTmsApi}>{children}</MutationsProvider>
            {ReactQueryDevtools ? (
              <Suspense fallback={null}>
                <ReactQueryDevtools initialIsOpen={false} />
              </Suspense>
            ) : null}
          </QueryClientProvider>
        </UIProviders>
      </ApiProvider>
    </MsalInstanceProvider>
  );
};
